/**
 * Overlay layer for the sticker picker's dialogs (options sheet, delete
 * confirmation, the sticker maker and its own small dialogs).
 *
 * Why not ModalShell: the composer closes the media picker on any mousedown
 * outside it and on Escape (ChatPane), which would unmount a dialog opened
 * from the picker mid-click. Each layer here is its own React root on <body>
 * that stops `mousedown` (and Escape) at its root, so working in a dialog never
 * tears down the picker — or the dialog. `pointerdown` and other keys still
 * bubble, so the vault auto-lock keeps seeing activity, and a layer closes
 * itself when the vault locks. Markup and classes mirror ModalShell, so the
 * look (dark + light themes, sheet on phones) is the app's.
 */
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { IconClose } from "@veto/ui";
import { getAtRestCipher } from "@veto/storage";

let openCount = 0;
let savedOverflow = "";
/** Where focus goes back to when a layer closes (per layer host). */
const returnFocus = new WeakMap<HTMLElement, HTMLElement | null>();
const closingHosts = new WeakSet<HTMLElement>();

function focusToReturnTo(): HTMLElement | null {
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const owner = active?.closest<HTMLElement>(".sticker-layer");
  // Opened from a layer that is closing (options sheet → confirm): go back where *it* came from.
  if (owner && closingHosts.has(owner)) return returnFocus.get(owner) ?? null;
  return active;
}

/** Mount `render` in a fresh layer; resolves with the value passed to `close` (or `dismissed`). */
export function openLayer<T>(render: (close: (value: T) => void) => ReactNode, dismissed: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const host = document.createElement("div");
    host.className = "sticker-layer";
    document.body.appendChild(host);
    const root = createRoot(host); // React's listeners on `host` come first…
    const stopMouse = (e: Event) => e.stopPropagation(); // …then nothing reaches window
    const stopEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") e.stopPropagation();
    };
    host.addEventListener("mousedown", stopMouse);
    host.addEventListener("keydown", stopEscape);
    returnFocus.set(host, focusToReturnTo());
    if (openCount++ === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    // Lock / unlock with another key while open → close (never shown over the lock screen).
    const cipherAtOpen = getAtRestCipher();
    const watch = cipherAtOpen
      ? window.setInterval(() => {
          if (getAtRestCipher() !== cipherAtOpen) close(dismissed);
        }, 400)
      : 0;
    let closed = false;
    function close(value: T) {
      if (closed) return;
      closed = true;
      closingHosts.add(host);
      if (watch) window.clearInterval(watch);
      // Unmount after the current event finishes dispatching.
      window.setTimeout(() => {
        const active = document.activeElement;
        const focusWasHere = !active || active === document.body || host.contains(active);
        root.unmount();
        host.removeEventListener("mousedown", stopMouse);
        host.removeEventListener("keydown", stopEscape);
        host.remove();
        if (--openCount === 0) document.body.style.overflow = savedOverflow;
        // Only give focus back if nothing else (e.g. a follow-up dialog) has taken it.
        const back = returnFocus.get(host);
        if (focusWasHere && back?.isConnected) back.focus({ preventScroll: true });
      }, 0);
      resolve(value);
    }
    root.render(render(close));
  });
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapTab(e: KeyboardEvent, card: HTMLElement | null) {
  if (!card) return;
  const items = [...card.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
  if (items.length === 0) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  if (e.shiftKey && (active === first || active === card)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

export type LayerSize = "sm" | "md" | "sheet";

/** ModalShell look-alike rendered inside a layer (see openLayer). */
export function LayerDialog({
  title,
  onClose,
  children,
  size = "md",
  footer,
  headerActions,
  className = "",
  backdropClassName = "",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: LayerSize;
  footer?: ReactNode;
  /** Extra header buttons, before Close (e.g. Undo / Done). */
  headerActions?: ReactNode;
  className?: string;
  backdropClassName?: string;
}) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, []);
  const sizeClass =
    size === "sm" ? "modal-card-sm" : size === "sheet" ? "modal-card-sheet" : "modal-card-md";
  return (
    <div
      className={`modal-backdrop${size === "sheet" ? " modal-backdrop-sheet" : ""} ${backdropClassName}`.trim()}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        className={`card modal-card ${sizeClass} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          } else if (e.key === "Tab") {
            trapTab(e, cardRef.current);
          }
        }}
      >
        {size === "sheet" && <div className="modal-grabber" aria-hidden />}
        <header className="modal-head">
          <h2 id={titleId} className="modal-title">
            {title}
          </h2>
          <span className="modal-title-spacer" />
          {headerActions}
          <button type="button" className="icon-btn modal-close" aria-label="Close" title="Close" onClick={onClose}>
            <IconClose />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

/** Confirmation dialog in its own layer; resolves true when confirmed. */
export function confirmInLayer(opts: { title: string; body: string; action: string; danger?: boolean }): Promise<boolean> {
  return openLayer<boolean>(
    (close) => (
      <LayerDialog
        title={opts.title}
        size="sm"
        onClose={() => close(false)}
        className="sticker-confirm"
        footer={
          <div className="sticker-dialog-actions">
            <button type="button" className="btn secondary small" onClick={() => close(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn small${opts.danger ? " danger" : ""}`}
              onClick={() => close(true)}
            >
              {opts.action}
            </button>
          </div>
        }
      >
        <p className="muted sticker-confirm-body">{opts.body}</p>
      </LayerDialog>
    ),
    false,
  );
}

export interface LayerChoice {
  id: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
}

/** Bottom sheet of actions (long-press menu); resolves with the chosen id or null. */
export function chooseInLayer(title: string, choices: LayerChoice[]): Promise<string | null> {
  return openLayer<string | null>(
    (close) => (
      <LayerDialog title={title} size="sheet" onClose={() => close(null)} className="chat-menu-sheet">
        <div className="chat-menu-list">
          {choices.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`chat-menu-row${c.danger ? " danger" : ""}`}
              onClick={() => close(c.id)}
            >
              {c.icon}
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      </LayerDialog>
    ),
    null,
  );
}
